// As três etapas da redefinição de senha da tela /login. Vivem aqui, e não no
// index.js, porque aquele arquivo já passa de 780 linhas com quatro etapas.
// Não há mockup deste fluxo: a casca é a mesma das etapas de 2FA e de
// confirmação de dispositivo (mesmo cartão, mesmo stepIcon, mesmo backLink).
import React, { useState } from "react";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import LockOpenOutlinedIcon from "@material-ui/icons/LockOpenOutlined";

import { i18n } from "../../translate/i18n";
import { sanitizeOtp, senhasConferem, passwordStrength } from "./authRules";

const ResetPasswordSteps = ({
  step,
  classes: c,
  OtpInput,
  email,
  onEmailChange,
  otp,
  onOtpChange,
  requiresTwoFactor,
  resendSeconds,
  onSend,
  onVerify,
  onSubmit,
  onResend,
  onBack,
  loading,
}) => {
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [totp, setTotp] = useState("");

  const forca = passwordStrength(senha);
  const senhaOk =
    senhasConferem(senha, confirmacao) &&
    (!requiresTwoFactor || sanitizeOtp(totp).length === 6);

  const voltar = (
    <button type="button" className={c.backLink} onClick={onBack}>
      <ArrowBackIcon />
      {i18n.t("login.reset.back")}
    </button>
  );

  if (step === "email") {
    return (
      <form
        className={c.formCard}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onSend();
        }}
      >
        {voltar}
        <div className={c.stepIcon}>
          <LockOpenOutlinedIcon />
        </div>
        <div>
          <div className={c.formTitle} style={{ fontSize: 24 }}>
            {i18n.t("login.reset.emailTitle")}
          </div>
          <div className={c.formSubtitle}>
            {i18n.t("login.reset.emailSubtitle")}
          </div>
        </div>

        <div>
          <label className={c.label} htmlFor="reset-email">
            {i18n.t("login.reset.emailLabel")}
          </label>
          <TextField
            id="reset-email"
            variant="outlined"
            className={c.input}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            autoComplete="email"
            autoFocus
            inputProps={{ "aria-label": i18n.t("login.reset.emailLabel") }}
          />
        </div>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={c.gradientButton}
          disabled={loading || !String(email || "").trim()}
        >
          {loading ? <CircularProgress size={22} /> : i18n.t("login.reset.send")}
        </Button>
      </form>
    );
  }

  if (step === "code") {
    return (
      <form
        className={c.formCard}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onVerify();
        }}
      >
        {voltar}
        <div className={c.stepIcon}>
          <LockOpenOutlinedIcon />
        </div>
        <div>
          <div className={c.formTitle} style={{ fontSize: 24 }}>
            {i18n.t("login.reset.codeTitle")}
          </div>
          <div className={c.formSubtitle}>
            {i18n.t("login.reset.codeSubtitle")}
          </div>
        </div>

        <OtpInput
          value={otp}
          onChange={onOtpChange}
          classes={c}
          autoFocus
          onEnter={onVerify}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={c.gradientButton}
          disabled={loading || sanitizeOtp(otp).length < 6}
        >
          {i18n.t("login.reset.verify")}
        </Button>

        {/* Eco da trava de 60s do servidor: sem ele o usuário só descobriria
            o throttle clicando e tomando erro. */}
        <div className={c.footNote}>
          <button
            type="button"
            className={c.ghostLink}
            onClick={onResend}
            disabled={resendSeconds > 0}
          >
            {resendSeconds > 0
              ? i18n.t("login.reset.resendIn", { seconds: resendSeconds })
              : i18n.t("login.reset.resend")}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form
      className={c.formCard}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(senha, requiresTwoFactor ? sanitizeOtp(totp) : undefined);
      }}
    >
      {voltar}
      <div className={c.stepIcon}>
        <LockOpenOutlinedIcon />
      </div>
      <div>
        <div className={c.formTitle} style={{ fontSize: 24 }}>
          {i18n.t("login.reset.passwordTitle")}
        </div>
        <div className={c.formSubtitle}>
          {i18n.t("login.reset.passwordSubtitle")}
        </div>
      </div>

      <div className={c.fields}>
        <div>
          <label className={c.label} htmlFor="reset-password">
            {i18n.t("login.reset.passwordLabel")}
          </label>
          <TextField
            id="reset-password"
            type="password"
            variant="outlined"
            className={c.input}
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="new-password"
            autoFocus
            inputProps={{ "aria-label": i18n.t("login.reset.passwordLabel") }}
          />
          <div className={c.strengthTrack} aria-hidden="true">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className={c.strengthSegment}
                style={
                  n <= forca.score
                    ? {
                        backgroundColor:
                          forca.score >= 4
                            ? "#34D399"
                            : forca.score === 3
                            ? "#22D3EE"
                            : forca.score === 2
                            ? "#FBBF24"
                            : "#F87171",
                      }
                    : undefined
                }
              />
            ))}
          </div>
          <div className={c.strengthLabel}>{forca.label}</div>
        </div>

        <div>
          <label className={c.label} htmlFor="reset-confirm">
            {i18n.t("login.reset.confirmLabel")}
          </label>
          <TextField
            id="reset-confirm"
            type="password"
            variant="outlined"
            className={c.input}
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            autoComplete="new-password"
            error={confirmacao.length > 0 && senha !== confirmacao}
            helperText={
              confirmacao.length > 0 && senha !== confirmacao
                ? i18n.t("login.reset.mismatch")
                : " "
            }
            inputProps={{ "aria-label": i18n.t("login.reset.confirmLabel") }}
          />
        </div>

        {requiresTwoFactor && (
          <div>
            <label className={c.label} htmlFor="reset-totp">
              {i18n.t("login.reset.totpLabel")}
            </label>
            <TextField
              id="reset-totp"
              variant="outlined"
              className={c.input}
              value={totp}
              onChange={(e) => setTotp(sanitizeOtp(e.target.value))}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                inputMode: "numeric",
                "aria-label": i18n.t("login.reset.totpLabel"),
              }}
            />
            <div className={c.formSubtitle}>{i18n.t("login.reset.totpHint")}</div>
          </div>
        )}
      </div>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        className={c.gradientButton}
        disabled={loading || !senhaOk}
      >
        {loading ? <CircularProgress size={22} /> : i18n.t("login.reset.submit")}
      </Button>
    </form>
  );
};

export default ResetPasswordSteps;
